import { authTypes } from '../types';
import { userTypes } from '../types';
import * as api from '@lib/apis/auth';
import axiosInstance from '@lib/apis/axiosInstance';
import { getApiErrorMessage } from '@lib/apis/apiErrorMessage';
import { getUser, normalizeUserProfile } from '@lib/apis/resources/user';
import { getUserAppAccessRecords } from '@lib/apis/resources/userAppAccess';
import { getCurrentAppId } from '@lib/apis/utilities';
import { API_BASE_URL } from '@lib/apis/config';
import { clearSentryUser } from '@lib/sentry';
import { clearSignedInState } from '@juristai/shared/src/auth/clearSignedInState';
import {
  ACCESS_LEVEL_HIERARCHY,
  isExplicitProSeUser,
  normalizeAccessLevel,
  resolveOrganizationAccessLevel,
} from '@lib/utils/accessLevel';

const safeParseStoredUser = () => {
  try {
    const raw = localStorage.getItem('user');
    return raw && raw !== 'undefined' ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const dedupeAccessRecords = (records = []) =>
  Array.from(
    new Map(
      (Array.isArray(records) ? records : [])
        .filter(Boolean)
        .map((record) => [
          record?.userAppAccessId || `${record?.userId || ''}:${record?.appId || ''}`,
          record,
        ])
    ).values()
  );

const rankAccessRecord = (record) =>
  ACCESS_LEVEL_HIERARCHY.indexOf(normalizeAccessLevel(record?.accessLevel || record?.access_level));

const pickHighestPrivilegeAccessRecord = (records = []) =>
  dedupeAccessRecords(records)
    .filter((record) => rankAccessRecord(record) !== -1)
    .sort((left, right) => rankAccessRecord(left) - rankAccessRecord(right))[0] || null;

const POST_SIGNUP_REDIRECT_KEY = 'postSignupRedirectUrl';

export const hydrateUserSessionUser = async (
  baseUser,
  { email, userId, appId, accessToken, refreshProfile = true } = {}
) => {
  const activeAppId = appId || getCurrentAppId();
  const normalizedBaseUser = normalizeUserProfile(baseUser);
  const resolvedEmail = email || normalizedBaseUser?.email;
  const resolvedUserId =
    userId ||
    normalizedBaseUser?.cognito_user_id ||
    normalizedBaseUser?.userId;

  if (!resolvedEmail || !resolvedUserId) {
    return normalizedBaseUser;
  }

  const [profileResponse, accessRecords] = await Promise.all([
    refreshProfile
      ? getUser({
          email: resolvedEmail,
          userId: resolvedUserId,
          appId: activeAppId,
          accessToken,
        }).catch((error) => {
          // A swallowed failure here is not cosmetic: the profile carries
          // organizationId, and without it every org-gated surface (Billing,
          // Manage Subscriptions, Manage Organization) silently disappears.
          console.error('User profile refresh failed during hydration:', error);
          return null;
        })
      : Promise.resolve(null),
    getUserAppAccessRecords({
      userId: resolvedUserId,
      appId: activeAppId,
      accessToken,
    }).catch((error) => {
      console.error('App access refresh failed during hydration:', error);
      return [];
    }),
  ]);

  const refreshedProfile =
    profileResponse?.success && profileResponse?.data?.user
      ? normalizeUserProfile(profileResponse.data.user)
      : null;

  const dedupedAccessRecords = dedupeAccessRecords(accessRecords);
  const currentAppAccessRecord = pickHighestPrivilegeAccessRecord(
    dedupedAccessRecords.filter((record) => String(record?.appId) === String(activeAppId))
  );
  const preferredAccessRecord = currentAppAccessRecord;
  const hasOrganizationContext = Boolean(
    refreshedProfile?.organizationId || normalizedBaseUser?.organizationId,
  );
  const compatibilityOrganizationAccessLevel =
    !isExplicitProSeUser(refreshedProfile || normalizedBaseUser) && hasOrganizationContext
      ? normalizeAccessLevel(
          preferredAccessRecord?.accessLevel || preferredAccessRecord?.access_level,
        )
      : '';
  const organizationAccessLevel =
    resolveOrganizationAccessLevel(refreshedProfile, { appId: activeAppId }) ||
    resolveOrganizationAccessLevel(normalizedBaseUser, { appId: activeAppId }) ||
    compatibilityOrganizationAccessLevel;

  // Hydration must never be able to *downgrade* a session. A failed or partial
  // profile refresh should leave org context exactly as it was, not blank it.
  const resolvedOrganizationId =
    refreshedProfile?.organizationId || normalizedBaseUser?.organizationId || undefined;

  return normalizeUserProfile({
    ...normalizedBaseUser,
    ...(refreshedProfile || {}),
    ...(resolvedOrganizationId ? { organizationId: resolvedOrganizationId } : {}),
    profileImage:
      refreshedProfile?.profileImage || normalizedBaseUser?.profileImage || '',
    userAppAccess: preferredAccessRecord || null,
    allUserAppAccesses: dedupedAccessRecords,
    accessLevel:
      preferredAccessRecord?.accessLevel ||
      preferredAccessRecord?.access_level ||
      refreshedProfile?.accessLevel ||
      normalizedBaseUser?.accessLevel ||
      '',
    access_level:
      preferredAccessRecord?.accessLevel ||
      preferredAccessRecord?.access_level ||
      refreshedProfile?.access_level ||
      normalizedBaseUser?.access_level ||
      '',
    organizationAccessLevel:
      organizationAccessLevel || normalizedBaseUser?.organizationAccessLevel || '',
    organization_access_level:
      organizationAccessLevel || normalizedBaseUser?.organization_access_level || '',
  });
};

export const hydrateAuthenticatedUser = ({ refreshProfile = true, appId } = {}) => async (
  dispatch,
  getState
) => {
  try {
    const activeAppId = appId || getCurrentAppId();
    const state = getState?.() || {};
    const isAuthenticated = Boolean(state?.auth?.isAuthenticated);
    const baseUser = state?.user || state?.auth?.user || safeParseStoredUser();

    if (!isAuthenticated || !baseUser) {
      return { success: false, skipped: true };
    }

    const hydratedUser = await hydrateUserSessionUser(baseUser, {
      email: baseUser?.email,
      userId: baseUser?.cognito_user_id || baseUser?.userId,
      appId: activeAppId,
      accessToken: state?.auth?.access,
      refreshProfile,
    });

    localStorage.setItem('user', JSON.stringify(hydratedUser));
    dispatch({ type: userTypes.SET_USER, payload: hydratedUser });

    return { success: true, data: hydratedUser };
  } catch (error) {
    console.error('Error hydrating authenticated user:', error);
    return {
      success: false,
      message: getApiErrorMessage(error, 'Unable to refresh account access.'),
    };
  }
};

export const registerEmail = (
  email,
  password1,
  password2,
  firstName,
  lastName,
  redirectUrl,
  inviteToken,
  options = {}
) => async (dispatch) => {
  try {
    const response = await api.registerEmail(
      email,
      password1,
      password2,
      firstName,
      lastName,
      redirectUrl,
      inviteToken,
      options
    );
    if (response.success) {
      localStorage.removeItem('access');
      localStorage.removeItem('access_expiration');
      localStorage.removeItem('refresh');
      localStorage.removeItem('refresh_expiration');
      localStorage.removeItem('user');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('chatToken');
      return {
        success: true,
        message: 'Success! Please check your email to confirm.',
      };
    }

    return {
      success: false,
      message: response?.message || 'Error registering email. Please try again.',
    };
  } catch (error) {
    console.error('Error registering email:', error);
    return {
      success: false,
      message: getApiErrorMessage(error, 'Error registering email. Please try again.'),
    };
  }
};

export const logInEmail = (email, password, options = {}) => async (dispatch) => {
  const {
    suppressNavigate,
    googleCredential,
    microsoftCredential,
    microsoftNonce,
    microsoftSignupOptions,
    oktaCredential,
    oktaNonce,
    samlResponse,
  } = options || {};
  void suppressNavigate;

  try {
    const response = samlResponse
      ? { success: true, data: samlResponse }
      : googleCredential
      ? await api.logInGoogle(googleCredential)
      : microsoftCredential
        ? await api.logInMicrosoft(microsoftCredential, microsoftNonce, microsoftSignupOptions)
        : oktaCredential
          ? await api.logInOkta(oktaCredential, oktaNonce)
        : await api.logInEmail(email, password);
    if (response.success) {
      const data = response.data;
      const sanitizedUser = normalizeUserProfile(data?.user);
      const signupRedirectUrlRaw =
        sanitizedUser?.signup_redirect_url ||
        sanitizedUser?.signup_url_redirect ||
        data?.signup_url_redirect ||
        data?.signup_redirect_url;

      const signupRedirectUrl =
        typeof signupRedirectUrlRaw === 'string' && signupRedirectUrlRaw.trim().length > 0
          ? signupRedirectUrlRaw.trim()
          : null;

      let user =
        signupRedirectUrl && sanitizedUser
          ? { ...sanitizedUser, signup_redirect_url: null, signup_url_redirect: null }
          : sanitizedUser;

      const resolvedEmail = user?.email || email;
      const resolvedUserId = user?.cognito_user_id || user?.userId;

      localStorage.setItem('access', data.access);
      localStorage.setItem('access_expiration', data.access_expiration);
      localStorage.setItem('refresh', data.refresh);
      localStorage.setItem('refresh_expiration', data.refresh_expiration);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('isAuthenticated', true);

      // Keep the product selected during signup available until the dashboard
      // has mounted. This is needed when email confirmation sends the user
      // through the home dashboard before the destination app is opened.
      if (signupRedirectUrl) {
        localStorage.setItem(POST_SIGNUP_REDIRECT_KEY, signupRedirectUrl);
      }

      if (data.chat_token) {
        localStorage.setItem('chatToken', data.chat_token);
      }

      if (resolvedEmail && resolvedUserId) {
        user = await hydrateUserSessionUser(user, {
          email: resolvedEmail,
          userId: resolvedUserId,
          appId: getCurrentAppId(),
          accessToken: data.access,
          refreshProfile: true,
        });
      }
      localStorage.setItem('user', JSON.stringify(user));

      dispatch({
        type: authTypes.LOG_IN,
        payload: {
          access: data.access,
          access_expiration: data.access_expiration,
          refresh: data.refresh,
          refresh_expiration: data.refresh_expiration,
          user,
          isAuthenticated: true,
          chatToken: data.chat_token,
        },
      });

      if (signupRedirectUrl && data.access) {
        try {
          await api.clearSignupRedirectUrl(data.access);
        } catch (error) {
          console.error('Failed to clear signup redirect URL:', error);
        }
      }

      return {
        success: true,
        data: { ...data, user },
        signupRedirectUrl,
      };
    }

    return {
      success: false,
      message: response.message,
      code: response.code,
      pending_token: response.pending_token,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Error logging in. Please try again.'),
      code: error?.code,
    };
  }
};

export const logInGoogle = (credential) => async (dispatch) =>
  dispatch(logInEmail('', '', { googleCredential: credential }));

export const logInMicrosoft = (idToken, nonce, signupOptions = {}) => async (dispatch) =>
  dispatch(logInEmail('', '', {
    microsoftCredential: idToken,
    microsoftNonce: nonce,
    microsoftSignupOptions: signupOptions,
  }));
export const logInOkta = (idToken, nonce) => async (dispatch) =>
  dispatch(logInEmail('', '', { oktaCredential: idToken, oktaNonce: nonce }));
export const logInSaml = (code, state) => async (dispatch) => {
  const response = await api.logInSaml(code, state);
  return response?.success ? dispatch(logInEmail('', '', { suppressNavigate: true, samlResponse: response.data })) : response;
};

export const logOut = () => async (dispatch) => {
  try {
    // The site's only sign-out: clear what every practice area stores.
    clearSignedInState({ extraKeys: [POST_SIGNUP_REDIRECT_KEY] });
    clearSentryUser();

    dispatch({ type: authTypes.LOG_OUT, payload: null });
  } catch (error) {
    console.error('Error logging out:', error);
  }
};

export const refreshToken = () => async (dispatch, getState) => {
  const URI = `${API_BASE_URL}/auth/token/refresh/`;

  try {
    const response = await axiosInstance.post(URI, {
      refresh: getState().auth.refresh,
    });

    if (response.status !== 200) {
      dispatch({
        type: authTypes.REFRESH_TOKEN_FAILURE,
        payload: { access: null, refresh: null, isAuthenticated: false },
      });
      dispatch({ type: authTypes.LOG_OUT, payload: null });
    } else {
      dispatch({
        type: authTypes.REFRESH_TOKEN_SUCCESS,
        payload: {
          access: response.data.access,
          access_expiration: response.data.access_expiration,
          isAuthenticated: true,
        },
      });
    }
  } catch (error) {
    console.error('Error refreshing token:', error);
    dispatch({ type: authTypes.LOG_OUT, payload: null });
  }
};
